import IconLink from "@shared/components/icons/Link"
import IconPaperclip from "@shared/components/icons/Paperclip"
import type { Link } from "@tiptap-content/extensions/link/link-attrs"
import { NodeViewWrapper, type ReactNodeViewProps } from "@tiptap/react"
import { Loading01 } from "@untitledui/icons"
import { startDragPreview } from "../../editor-ui/drag-handle/DragPreview"
import { selectNodeAsDragMechanics } from "../drag-mechanics-selection/drag-mechanics-selection-extension"
import styles from "./link.module.css"

// In its own file (with only component exports) so that edits hot-reload
// via React Fast Refresh instead of reloading the page.

/**
 * The one node view for every Link form; branches on attrs.renderAs
 * (docs/unified-links.md §5).
 */
export const LinkComponent = (props: ReactNodeViewProps) => {
  const { editor, getPos } = props
  const attrs = props.node.attrs as Link
  const isBlockForm = attrs.renderAs === "block" || attrs.renderAs === "preview"

  /**
   * The token's anchor is its own drag affordance (no handle), like the old
   * linkPreview: tag the selection as drag mechanics (no highlight, no menus) and swap
   * in the Trello-style drag preview. At dragstart — not mousedown — so a plain click
   * stays a click.
   */
  const handleDragStart = (event: React.DragEvent<HTMLAnchorElement>) => {
    const pos = getPos()
    if (pos !== undefined) selectNodeAsDragMechanics(editor, pos)

    const nodeDom = event.currentTarget.closest("[data-node-view-wrapper]")
    if (nodeDom instanceof HTMLElement) startDragPreview(event.nativeEvent, nodeDom)
  }

  const label = attrs.title ?? attrs.url

  // Icons per §5: the file's own icon (Drive/Dropbox iconUrl) when present, else a
  // paperclip for files and a link icon for web links. The hyperlink form has none.
  const icon = !attrs.url ? (
    <span className="flex h-[1lh] shrink-0 items-center">
      <Loading01 aria-hidden="true" className="size-[15px] animate-spin" />
    </span>
  ) : attrs.renderAs === "hyperlink" ? null : attrs.icon ? (
    <img className={styles.icon} src={attrs.icon} alt="" draggable={false} />
  ) : attrs.type === "link" ? (
    <IconLink className={styles.icon} />
  ) : (
    <IconPaperclip className={styles.icon} />
  )

  return (
    // Inline atom: the wrapper must be inline (span) or it breaks text flow.
    <NodeViewWrapper as={isBlockForm ? "div" : "span"}>
      <a
        href={attrs.url || undefined}
        role={!attrs.url ? "status" : undefined}
        aria-label={!attrs.url ? `Uploading ${label}` : undefined}
        aria-busy={!attrs.url}
        title={attrs.url}
        target="_blank"
        rel="noopener noreferrer"
        data-is-tiptap-link=""
        className={
          isBlockForm
            ? styles.blockLine
            : attrs.renderAs === "chip"
              ? styles.chip
              : styles.hyperlink
        }
        // The anchor drags the node, not the URL. Block form: draggable + the below
        // dragstart handler start an element drag (like the old linkPreview). Inline
        // form: no draggable attr — ProseMirror makes the node view draggable itself
        // (spec draggable) and owns the drag. Both keep data-drag-handle so the
        // dragstart passes Tiptap's NodeView.stopEvent.
        draggable={isBlockForm ? "true" : undefined}
        data-drag-handle
        onDragStart={handleDragStart}
      >
        {icon}
        {isBlockForm ? (
          <span className={styles.blockLineText}>{label}</span>
        ) : attrs.renderAs === "chip" ? (
          <span className={styles.chipLabel}>{label}</span>
        ) : (
          label
        )}
      </a>
    </NodeViewWrapper>
  )
}
